import { Component, Input, EventEmitter } from "@angular/core";
import { LanguageService } from "../services/language.service";
import { EditorContentService } from "../services/editorcontent.service";
import { Ace } from "app/lib/ace-builds/src-noconflict/ace";

import { LoginService } from "../services/login.service";
import { DebuggerService } from "app/services/debugger.service";
import { MultipleChoiceOption, Task, TaskType } from "app/lib/Task";
import { Exam } from "app/lib/Exam";
import { ExamService } from "app/services/exam.service";
import { CanvasService } from "app/services/canvas.service";
import { CommandsService } from "app/services/commands.service";

import { ANTLRInputStream, CommonTokenStream } from "antlr4ts";
import { ProgDecl } from "app/xlogo-parser/ir/ast";
import { XLogoLexer, XLogoParser } from "app/xlogo-parser/parser";
import {
  XLogoParserErrorStrategy,
  AstTranslatorVisitor,
  ProgDeclVerifier,
  SemanticVerifier,
  SemanticErrorCause,
  ANTLRSyntaxErrorListener,
  SyntaxErrorCause,
  XLogoSemanticError,
  XLogoSyntaxError,
  RuntimeError,
} from "app/xlogo-parser";
import { InterpreterService } from "app/services/interpreter.service";

declare var require: (...any) => any;
declare var swal: any;
const sha256 = require("sha256");

@Component({
  selector: "editor-component",
  templateUrl: "./editor.component.html",
})
export class EditorComponent {
  @Input("resizeEmitter") resizeEmitter: EventEmitter<void>;

  // the editor
  private editor: Ace.Editor;

  // the current exam (if any)
  private exam: Exam = null;

  // the currently selected task (if any, only for exams)
  private selectedTask: Task = null;

  // the selected option of the currently selected task of type multiplechoice (if there is any, only for exam)
  private selectedOpt: MultipleChoiceOption;

  // all possible options of the currently selected task of type multiplechoice (if there is any, only for exam)
  private mcOptionsArray: MultipleChoiceOption[] = null;

  private readonly SOURCE_NAME: string = "editor";

  // whether a changed in the editor text happened programmatically (e.g. set code template for a task during exam)
  private editorTextChangedProgrammatically: boolean = false;

  //UI stuff
  private HEIGHT: string = "97%";
  private WIDTH: string = "100%";

  //TODO: can this be removed?
  private mcOptions: MultipleChoiceOption[] = [];

  // Programs that have been parsed by the parser
  public parsedPrograms: Map<string, ProgDecl> = new Map();

  private lastStartedParser: number = 0;

  constructor(
    private languageService: LanguageService,
    private editorContentService: EditorContentService,
    private examService: ExamService,
    private loginService: LoginService,
    private debuggerService: DebuggerService,
    private canvasService: CanvasService,
    private commandsService: CommandsService,
    private interpreterService: InterpreterService
  ) {}

  ngOnInit() {
    // load editor
    this.editor = (<any>window).ace.edit("editor");
    console.log(this.editor);
    this.editor.setTheme("ace/theme/kr_theme");
    this.editor.getSession().setMode("ace/mode/python");
    this.editor.setOption("tabSize", 2);

    // TODO: Update Ace and remove this untyping
    // This VERY UGLY hack is necessary because the types do not match the version of the javascript file.
    // The ACE editor has to be updated such that the type definition is correct again.
    const untypedEditor: any = this.editor;
    untypedEditor.setOption("height", 300);

    this.editor.session.setUseWrapMode(true);
    this.editor.session.setOption("indentedSoftWrap", false);

    untypedEditor.commands.bindKeys({ "Ctrl-L": null, "Ctrl-F": null });
    untypedEditor.commands.bindKeys({ "Ctrl-Shift-7": "togglecomment" });

    this.editor
      .on("change", () => {
        this.onEditorChange();
      })
      .bind(this);

    // register on focus for editor
    untypedEditor.on(
      "focus",
      function () {
        this.editorContentService.onEditorFocus = true;
      }.bind(this),
      false
    );

    // register on blur for editor
    untypedEditor.on(
      "blur",
      function () {
        this.editorContentService.onEditorFocus = false;
      }.bind(this),
      false
    );

    //subscribe to emitter that informs us whether we need to adapt the editor text
    this.editorContentService.textInDisplayNeedsChangeEmitter.subscribe(() =>
      this.onEditorContentEvent()
    );

    //enables/disables the editor according to the corresponding events in the editorContentservice
    this.editorContentService.editorEnabler.subscribe((condition) => {
      if (condition) {
        this.enableEditor();
      } else {
        this.disableEditor();
      }
    });

    // React to events in the commandline (by saving backup mostly)
    // TODO: why? needed?
    this.commandsService.inputChangedEmitter.subscribe(() =>
      this.onInputLineEvent()
    );
    this.debuggerService.setInitialSessionMarkers(this.editor.session);
    this.debuggerService.setEditor(this.editor);

    // subscribe to known when resize has to be performed
    this.resizeEmitter.subscribe(() => {
      this.updateHeight();
    });

    // react to changes in the examMode (switch on/off)
    this.examService.examModeChangedEmitter.subscribe(() => this.onExamLoad());
    this.exam = this.examService.getExam();
    if (this.exam != null) {
      // if there is an exam, then retrieve its tasks
      this.onSelect(this.exam.getTasks()[0]);
    }

    //update the size (of the editor)
    this.updateHeight();

    this.commandsService.space.subscribe((value) => {
      this.commandsServiceForSubscription();
    });

    this.interpreterService.registerEventListenerStarted(
      this.onInterpreterStarted.bind(this)
    );
    this.interpreterService.registerEventListenerFinished(
      this.onInterpreterFinished.bind(this)
    );
  }

  public commandsServiceForSubscription() {
    if (
      this.examService.examModeIsOn() &&
      !this.examService.taskChangeHappening
    ) {
      if (this.selectedTask != null) {
        this.selectedTask.handedIn = false;
      }
    }
  }

  updateHeight(): void {
    // update the size of the editor
    setTimeout(() => {
      if (this.exam != null) {
        var grandParentHeight = (<HTMLDivElement>(
          document.getElementById("editorGrandParent")
        )).offsetHeight;
        if (this.selectedTask != null) {
          var uncle = <HTMLDivElement>document.getElementById("editorUncle");
          var uncleHeight = uncle.offsetHeight;
          var res = 0;
          var resWidth = uncle.offsetWidth;
          if (this.selectedTask.tasktype == TaskType.MC) {
            var mcOptionsDisplay = <HTMLDivElement>(
              document.getElementById("mcOptionsDisplay")
            );
            var mcHeight = mcOptionsDisplay.offsetHeight;
            var res = grandParentHeight - uncleHeight - mcHeight;
          } else {
            var res = grandParentHeight - uncleHeight;
          }
          resWidth *= 1.015;
          res *= 1.1;
          this.WIDTH = resWidth.toString() + "px";
          this.HEIGHT = res.toString() + "px";
        } else {
          this.HEIGHT = "75vh";
          this.WIDTH = "100%";
        }
      } else {
        this.HEIGHT = "97vh";
        this.WIDTH = "100%";
      }
    }, 50);
  }

  onExamLoad(): void {
    // actions to perform when exam is loaded (or destroyed)
    if (this.examService.examModeIsOn()) {
      this.exam = this.examService.getExam();
      this.canvasService.cs();
      this.editorContentService.setTextOfCurrentEditorProgrammatically("");
      this.commandsService.deleteAll();
      this.onSelect(this.exam.getTasks()[0]);
    } else {
      this.exam = null;
      this.selectedTask = null;
      this.selectedOpt = null;
      this.editorContentService.setTextOfCurrentEditorProgrammatically("");
      this.updateHeight();
    }
  }

  //programmatically set editor text
  onEditorContentEvent() {
    this.editorTextChangedProgrammatically = true;
    this.setEditorValue(this.editorContentService.getCurrentEditorText());
  }

  // set input line
  onInputLineEvent() {
    if (
      this.selectedTask != null &&
      this.examService.examModeIsOn() &&
      !this.examService.taskChangeHappening
    ) {
      this.selectedTask.setInputLine(this.commandsService.getCommandLineText());
      //trigger backup save
      this.examService.saveBackupToServer().then(
        (cond) => {
          if (cond) {
            console.log("Successfully save backup to server");
          } else {
            console.log("Backup information is recent enough");
          }
        },
        (cond) => {
          if (cond) {
            console.log("Failed to save backup to server");
          }
        }
      );
    }
  }

  // process changes made by pupil
  public onEditorChange(): void {
    if (!this.editorTextChangedProgrammatically) {
      //changes are really by pupil. Also programmatically setting the editor text triggers the corresponding event.
      this.editorContentService.setTextOfCurrentEditorFromDisplay(
        this.getEditorValue()
      );
      if (this.selectedTask != null) {
        this.selectedTask.modified =
          this.selectedTask.modified || this.selectedTask.handedIn;
        this.selectedTask.handedIn = false;
        this.selectedTask.setEditorContent(this.getEditorValue());
        this.selectedTask.setInputLine(
          this.commandsService.getCommandLineText()
        );
      }
    }
    this.editorTextChangedProgrammatically = false;
    // Start parsing

    // Syntax- & Semantic error markers
    this.editor.session.clearAnnotations();
    const markers: { [index: number]: Ace.MarkerLike } = <
      { [index: number]: Ace.MarkerLike }
    >this.editor.session.getMarkers(true);
    for (let marker in markers) {
      this.editor.session.removeMarker(markers[marker].id);
    }

    // Runtimeerror Marker
    const markersBack: { [index: number]: Ace.MarkerLike } = <
      { [index: number]: Ace.MarkerLike }
    >this.editor.session.getMarkers(false);
    for (let marker in markersBack) {
      this.editor.session.removeMarker(markersBack[marker].id);
    }

    this.interpreterService.setPrograms(
      this.SOURCE_NAME,
      this.getEditorValue().toLowerCase(),
      this.onParseFailure.bind(this)
    );

    //take backup only if currently taking an exam, otherwise stop here
    if (!this.examService.examModeIsOn()) {
      return;
    }

    //trigger backup save
    this.examService.saveBackupToServer().then(
      (cond) => {
        if (cond) {
          console.log("Successfully save backup to server");
        } else {
          console.log("Backup information is recent enough");
        }
      },
      (cond) => {
        if (cond) {
          console.log("Failed to save backup to server");
        }
      }
    );
  }

  private onParseFailure(
    syntaxErrors: XLogoSyntaxError[],
    semanticErrors: XLogoSemanticError[]
  ): void {
    this.editor.session.clearAnnotations();
    const markers: { [index: number]: Ace.MarkerLike } = <
      { [index: number]: Ace.MarkerLike }
    >this.editor.session.getMarkers(true);

    for (let marker in markers) {
      this.editor.session.removeMarker(markers[marker].id);
    }

    const errMsgs: Ace.Annotation[] = [];
    for (const err of syntaxErrors) {
      const error: Ace.Annotation = {
        column: err.token!.startIndex,
        row: err.token!.line - 1,
        text: this.languageService.translateKey(
          SyntaxErrorCause[err.cause],
          err.translationParameters
        ),
        type: "error",
      };
      errMsgs.push(error);
      const Range = (<any>window).ace.require("ace/range").Range;
      const range: Ace.Range = new Range(
        err.token!.line - 1,
        err.token!.charPositionInLine,
        err.token!.line - 1,
        err.token!.charPositionInLine + err.token!.text!.length
      );
      this.editor.session.addMarker(range, "markerSyntaxError", "text", true);
    }
    for (const err of semanticErrors) {
      let error: Ace.Annotation = {
        column: err.ast.Range!.StarColumn,
        row: err.ast.Range!.StartLine - 1,
        text: this.languageService.translateKey(
          SemanticErrorCause[err.cause],
          err.translationParameters
        ),
        type: "error",
      };

      errMsgs.push(error);
      const Range = (<any>window).ace.require("ace/range").Range;
      const range: Ace.Range = new Range(
        err.ast.Range!.StartLine - 1,
        err.ast.Range!.StarColumn,
        err.ast.Range!.EndLine - 1,
        err.ast.Range!.EndColumn
      );
      this.editor.session.addMarker(range, "markerSyntaxError", "text", true);
    }
    this.editor.session.setAnnotations(errMsgs);
  }

  private onInterpreterStarted(
    input: string,
    started_successfully: boolean,
    syntaxError: XLogoSyntaxError[],
    semanticError: XLogoSemanticError[]
  ) {
    const markers: { [index: number]: Ace.MarkerLike } = <
      { [index: number]: Ace.MarkerLike }
    >this.editor.session.getMarkers(false);
    for (let marker in markers) {
      this.editor.session.removeMarker(markers[marker].id);
    }

    if (started_successfully) this.disableEditor();
  }

  private onInterpreterFinished(
    input: string,
    runtimeError: RuntimeError | undefined
  ) {
    this.enableEditor();
    if (
      runtimeError &&
      runtimeError.ast &&
      runtimeError.ast.InputSource === this.SOURCE_NAME
    ) {
      const Range = (<any>window).ace.require("ace/range").Range;
      const range = new Range(
        runtimeError.ast!.Range!.StartLine - 1,
        runtimeError.ast!.Range!.StarColumn,
        runtimeError.ast!.Range!.EndLine - 1,
        runtimeError.ast!.Range!.EndColumn
      );
      this.editor.session.addMarker(range, "markerRuntimeError", "text", false);
    }
  }

  private getEditorValue(): string {
    return this.editor.getValue();
  }

  private setEditorValue(val: string) {
    this.editor.setValue(val, 1);
  }

  public onSelect(task: Task) {
    // if a new task is selected, then switch all the states using the corresponding method defined in the examservice
    this.examService.switchCanvasAndHistoryState(this.selectedTask, task);

    //set new task
    this.selectedTask = task;
    this.examService.setSelectedTask(this.selectedTask);
    this.mcOptionsArray = null;
    this.editorContentService.setTextOfCurrentEditorProgrammatically("");
    //initialize new task
    var prevContent: string = this.selectedTask.getEditorContent();
    switch (this.selectedTask.tasktype) {
      case TaskType.MC:
        this.mcOptionsArray = [];
        var skel: string[] = this.selectedTask.getSkeleton();
        var sol: string = this.selectedTask.getPupilSolution();
        if (sol == null || sol == "") {
          sol = this.selectedTask.getChosenButNotHandedInOption();
        }
        for (var i = 0; i < skel.length; i++) {
          var opt: MultipleChoiceOption = new MultipleChoiceOption();
          opt.text = skel[i];
          if (opt.text == sol) {
            this.selectedOpt = opt;
          }
          this.mcOptionsArray.push(opt);
        }
        if (prevContent != null) {
          this.editorContentService.setTextOfCurrentEditorProgrammatically(
            prevContent
          );
        }
        break;
      case TaskType.EXTEND_TEMPLATE:
        if (prevContent != null) {
          this.editorContentService.setTextOfCurrentEditorProgrammatically(
            prevContent
          );
        } else {
          this.editorContentService.setTextOfCurrentEditorProgrammatically(
            this.selectedTask.getSkeleton()[0]
          );
        }
        break;
      case TaskType.DRAWX:
        if (prevContent != null) {
          this.editorContentService.setTextOfCurrentEditorProgrammatically(
            prevContent
          );
        }
        break;
      default:
      //Do nothing
    }
    this.updateHeight();
  }

  public confirmCurrentTask(): void {
    //hand in the current task. Triggers saveSolution() and FORCES saveBackup()
    if (this.selectedTask != null) {
      if (!this.selectedTask.handedIn) {
        //The following line is to ensure that the history is also stored with the solution
        this.examService.switchCanvasAndHistoryState(
          this.selectedTask,
          this.selectedTask
        );
        switch (this.selectedTask.tasktype) {
          case TaskType.MC:
            if (this.selectedOpt != null) {
              this.selectedTask.setPupilSolution(this.selectedOpt.text);
            }
            break;
          case TaskType.EXTEND_TEMPLATE:
            this.selectedTask.setPupilSolution(this.getEditorValue());
            break;
          case TaskType.DRAWX:
            this.selectedTask.setPupilSolution(this.getEditorValue());
            break;
          default:
          //Do nothing
        }
        this.examService.saveSolutionToServer().then(
          () => {
            this.selectedTask.handedIn = true;
            this.selectedTask.modified = true;
            this.examService.saveBackupToServer(true).then(
              () => console.log("Successfully save backup to server"),
              () => console.log("Failed to save backup to server")
            );
            console.log("Successfully save solution to server");
          },
          () => console.log("Failed to save solution to server")
        );
      }
    }
  }

  disableEditor() {
    //diable editor: text cannot be changed and opacity is slightly see-through
    this.editor.setOptions({
      readOnly: true,
      highlightActiveLine: false,
      highlightGutterLine: false,
    });
    this.editor.container.style.opacity = "0.75";
  }

  enableEditor() {
    //enable editor: text can be changed and it is not see-through any longer
    this.editor.setOptions({
      readOnly: false,
      highlightActiveLine: true,
      highlightGutterLine: true,
    });
    this.editor.container.style.opacity = "1";
  }

  onSelectMcOption(opt: MultipleChoiceOption) {
    this.selectedOpt = opt;
    if (this.selectedTask != null) {
      this.selectedTask.modified =
        this.selectedTask.modified || this.selectedTask.handedIn;
      this.selectedTask.handedIn = false;
      this.selectedTask.setChosenButNotHandedInOption(this.selectedOpt.text);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/editor.component.ts